import type {
  ConfigProxyErrorResponse,
  ConfigValidationIssue,
  ConfigValidationResult,
} from '@shared/types/ConfigValidation';
import type { DetailConfigDocument } from '@shared/types/DetailConfig';
import { isPlainObject } from '@/Utils/Core/object';

const API_URL = import.meta.env.VITE_API_URL;

export type StoredDetailConfigResult =
  | { ok: true; document: DetailConfigDocument }
  | { ok: false; status: number; error: string; issues: ConfigValidationIssue[] };

function readIssues(body: unknown): ConfigValidationIssue[] {
  const issues = (body as Partial<ConfigProxyErrorResponse> | null)?.details?.issues;
  return Array.isArray(issues) ? issues : [];
}

export async function fetchStoredDetailConfig(url: string): Promise<StoredDetailConfigResult> {
  try {
    const response = await fetch(`${API_URL}/detail-config?url=${encodeURIComponent(url)}`);
    const body: unknown = await response.json().catch(() => null);
    if (response.ok && isPlainObject(body)) {
      return { ok: true, document: body as DetailConfigDocument };
    }

    return {
      ok: false,
      status: response.status,
      error: String(
        (body as Partial<ConfigProxyErrorResponse> | null)?.error ??
          (response.ok ? 'Invalid detail_config response' : response.statusText),
      ),
      issues: readIssues(body),
    };
  } catch (error) {
    return { ok: false, status: 0, error: String(error), issues: [] };
  }
}

export async function validateDetailConfig(document: unknown): Promise<ConfigValidationResult> {
  const response = await fetch(`${API_URL}/detail-config/validate`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(document),
  });
  if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);

  return (await response.json()) as ConfigValidationResult;
}
